/**
 * Tutorial Step Viewer - displays tutorial content step by step
 */

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { type MockData, extractSteps } from "#app/lib/tutorials/parser";
import { TutorialContent } from "./TutorialContent";

export interface TutorialStep {
  id: string;
  title: string;
  content: string;
  level: number;
}

export interface TutorialStepViewerProps {
  fullContent: string;
  currentStepIndex: number;
  mocks?: MockData[];
  onCheckpointComplete?: (id: string, correct: boolean) => void;
  completedCheckpoints?: string[];
  filterWorkflowSteps?: boolean; // If true, filter out workflow step descriptions
  workflowSteps?: Array<{ name: string; description: string }>; // Workflow steps to filter out
}

/**
 * Split content by headings to create individual step content
 */
export function getTutorialStepContents(content: string): TutorialStep[] {
  const steps = extractSteps(content);
  if (steps.length === 0) {
    return [{ id: "content", title: "Content", content, level: 1 }];
  }

  const headingRegex = /^(#{1,6})\s+(.+)$/gm;
  const headingMatches: Array<{
    index: number;
    level: number;
    title: string;
    id: string;
  }> = [];

  let match;
  while ((match = headingRegex.exec(content)) !== null) {
    const title = match[2]!.trim();
    const id = title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    headingMatches.push({
      index: match.index,
      level: match[1]!.length,
      title,
      id,
    });
  }

  // Sort by index
  headingMatches.sort((a, b) => a.index - b.index);

  // Extract content for each step and club empty steps with next
  const allSteps: Array<{
    id: string;
    title: string;
    content: string;
    level: number;
    isEmpty: boolean;
  }> = [];

  // First pass: identify all steps and mark empty ones
  for (let i = 0; i < headingMatches.length; i++) {
    const current = headingMatches[i]!;
    const next = headingMatches[i + 1];

    const startIndex = current.index;
    const endIndex = next ? next.index : content.length;

    const stepContent = content.substring(startIndex, endIndex).trim();
    const headingLine = stepContent.split("\n")[0] ?? "";
    const contentAfterHeading = stepContent
      .substring(headingLine.length)
      .trim();
    const isEmpty = contentAfterHeading.length === 0;

    allSteps.push({
      id: current.id,
      title: current.title,
      content: stepContent,
      level: current.level,
      isEmpty,
    });
  }

  // Second pass: club empty steps with next non-empty step
  const finalSteps: TutorialStep[] = [];
  let i = 0;

  while (i < allSteps.length) {
    const current = allSteps[i]!;

    if (current.isEmpty) {
      // Find next non-empty step to club with
      let nextNonEmptyIndex = i + 1;
      while (
        nextNonEmptyIndex < allSteps.length &&
        allSteps[nextNonEmptyIndex]!.isEmpty
      ) {
        nextNonEmptyIndex++;
      }

      if (nextNonEmptyIndex < allSteps.length) {
        // Club with next non-empty step
        const nextStep = allSteps[nextNonEmptyIndex]!;
        const combinedContent = `${current.content}\n\n${nextStep.content}`;
        finalSteps.push({
          id: current.id,
          title: `${current.title} & ${nextStep.title}`,
          content: combinedContent,
          level: current.level,
        });
        i = nextNonEmptyIndex + 1; // Skip the next step since we've combined it
      } else {
        // No non-empty step found, club with previous if exists
        if (finalSteps.length > 0) {
          const lastStep = finalSteps.at(-1)!;
          finalSteps[finalSteps.length - 1] = {
            ...lastStep,
            title: `${lastStep.title} & ${current.title}`,
            content: `${lastStep.content}\n\n${current.content}`,
          };
        } else {
          // First step is empty and no other steps, just add it
          finalSteps.push({
            id: current.id,
            title: current.title,
            content: current.content,
            level: current.level,
          });
        }
        i++;
      }
    } else {
      // Step has content, add it normally
      finalSteps.push({
        id: current.id,
        title: current.title,
        content: current.content,
        level: current.level,
      });
      i++;
    }
  }

  return finalSteps;
}

export function TutorialStepViewer({
  fullContent,
  currentStepIndex,
  mocks = [],
  onCheckpointComplete,
  completedCheckpoints = [],
  filterWorkflowSteps = false,
  workflowSteps = [],
}: TutorialStepViewerProps) {
  const { t } = useTranslation("docs");
  const stepContents = useMemo(
    () => getTutorialStepContents(fullContent),
    [fullContent],
  );

  const currentStep = stepContents[currentStepIndex];

  if (!currentStep) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>{t("stepNotFound")}</p>
      </div>
    );
  }

  // Filter out workflow step descriptions if filterWorkflowSteps is true
  let contentToShow = currentStep.content;
  if (filterWorkflowSteps && workflowSteps.length > 0) {
    // Remove entire code blocks that contain workflow flow diagrams
    // Pattern: Code blocks with "Step X:" and arrows
    contentToShow = contentToShow.replace(
      /```[\s\S]*?Step\s+\d+:[\s\S]*?```/gi,
      "",
    );

    // Remove workflow flow diagrams with arrows and branches (multiline pattern)
    // Pattern: "Step X:" followed by arrows and branches
    const flowDiagramRegex =
      /Step\s+\d+:\s*[^\n]*(?:\n\s*[↓→├└─→\s\[\]]*[^\n]*)*/gim;
    contentToShow = contentToShow.replace(flowDiagramRegex, "");

    // Remove lines with arrows and branching logic (including indented)
    contentToShow = contentToShow.replace(/^\s*[↓→├└─→\s]+.*$/gm, "");

    // Remove conditional branches (If approved/rejected/Continue)
    contentToShow = contentToShow.replace(
      /^\s*(├─→|└─→)\s*If\s+(approved|rejected|continue):\s*.*$/gim,
      "",
    );
    contentToShow = contentToShow.replace(
      /^\s*If\s+(approved|rejected|continue):\s*.*$/gim,
      "",
    );
    contentToShow = contentToShow.replace(/^\s*Continue\s*$/gim, "");

    // Remove step references in brackets like [Manager Approval]
    contentToShow = contentToShow.replace(/\[[^\]]*Approval[^\]]*\]/gi, "");
    contentToShow = contentToShow.replace(/\[[^\]]*Step\s+\d+[^\]]*\]/gi, "");

    // Remove numbered step lists that match workflow step names
    workflowSteps.forEach((ws) => {
      const stepName = ws.name;
      // Remove lines that start with number and contain the workflow step name
      const regex = new RegExp(
        `^\\d+\\.\\s+(\\*\\*)?${stepName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(\\*\\*)?[^\\n]*$`,
        "gmi",
      );
      contentToShow = contentToShow.replace(regex, "");
    });

    // Remove common workflow step patterns
    contentToShow = contentToShow.replace(/^\*\*.*?\*\*\s*[-–—]\s*.*$/gm, "");
    contentToShow = contentToShow.replace(/^\d+\.\s+\*\*.*?\*\*.*$/gm, "");
    contentToShow = contentToShow.replace(/^\d+\.\s+[A-Z][^\\n]*:.*$/gm, "");

    // Remove "The workflow will:" sections with numbered steps
    const workflowWillRegex =
      /(?:The workflow will|Workflow steps?|Steps?):?\s*\n(?:\d+\.\s+[^\n]+\n?)+/gi;
    contentToShow = contentToShow.replace(workflowWillRegex, "");

    // Remove any remaining numbered lists that look like workflow steps
    contentToShow = contentToShow.replace(/^\d+\.\s+[A-Z][^.\n]{10,}.*$/gm, "");

    // Remove any lines that are just step references
    contentToShow = contentToShow.replace(/^Step\s+\d+.*$/gim, "");

    // Clean up multiple empty lines and trim
    contentToShow = contentToShow.replace(/\n{3,}/g, "\n\n");
    contentToShow = contentToShow.trim();

    // If only heading remains, don't show content (React Flow is already shown)
    const headingOnly =
      contentToShow === `# ${currentStep.title}` ||
      contentToShow === currentStep.title ||
      contentToShow.length < 50;

    if (headingOnly) {
      return null;
    }
  }

  return (
    <div className="tutorial-step-content">
      <TutorialContent
        content={contentToShow}
        mocks={mocks}
        onCheckpointComplete={onCheckpointComplete}
        completedCheckpoints={completedCheckpoints}
      />
    </div>
  );
}
